import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { DollarSign, CheckCircle, Clock, FileText, Loader2 } from 'lucide-react';
import { getInitials } from '../../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context';
import AdjustmentsPanel from '../../components/finance/AdjustmentsPanel';

export default function FinanceDashboard() {
  const { user } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  // V14.5 Part 19z12 — Founder ask (2026-06-01): "Put an apply button
  // for the admin to click on once he makes changes."
  // Filter inputs are now "draft" state — user edits them freely,
  // nothing re-fetches. Clicking Apply commits the draft → applied
  // state, which is what drives the actual API call AND what we pass
  // to the AdjustmentsPanel (so it filters by the same window).
  const _initialMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  const _initialFrom = () => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  };
  const _initialTo = () => new Date().toISOString().slice(0, 10);

  // DRAFT state — bound to the form controls
  const [draftMode, setDraftMode] = useState('month');
  const [draftMonth, setDraftMonth] = useState(_initialMonth);
  const [draftFrom, setDraftFrom] = useState(_initialFrom);
  const [draftTo, setDraftTo] = useState(_initialTo);
  const [draftInstructor, setDraftInstructor] = useState('all');

  // APPLIED state — what's actually in flight + passed to children
  const [filterMode, setFilterMode] = useState('month');
  const [month, setMonth] = useState(_initialMonth);
  const [dateFrom, setDateFrom] = useState(_initialFrom);
  const [dateTo, setDateTo] = useState(_initialTo);
  const [instructorFilter, setInstructorFilter] = useState('all');

  // Is the form dirty? (any draft ≠ applied)
  const isDirty = (
    draftMode !== filterMode
    || draftInstructor !== instructorFilter
    || (draftMode === 'month' ? draftMonth !== month : (draftFrom !== dateFrom || draftTo !== dateTo))
  );

  const applyFilters = () => {
    setFilterMode(draftMode);
    setMonth(draftMonth);
    setDateFrom(draftFrom);
    setDateTo(draftTo);
    setInstructorFilter(draftInstructor);
  };

  const [tab, setTab] = useState('overview');
  const [instructors, setInstructors] = useState([]);
  const [configDialog, setConfigDialog] = useState({ open: false, instructor: null });
  const [configForm, setConfigForm] = useState({ rate_30min: 0, rate_45min: 0, rate_60min: 0, rate_90min: 0, no_show_rate: 0 });
  const [savingConfig, setSavingConfig] = useState(false);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const params = {
        instructor_id: instructorFilter !== 'all' ? instructorFilter : undefined,
      };
      if (filterMode === 'custom') {
        params.date_from = dateFrom;
        params.date_to = dateTo;
      } else {
        params.month = month;
      }
      const res = await api.get('/api/finance/instructor-payouts', { params });
      setPayouts(res.data.payouts || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // Re-fetch only when APPLIED state changes (not draft).
  useEffect(() => { fetchPayouts(); }, [month, instructorFilter, filterMode, dateFrom, dateTo]);
  useEffect(() => { api.get('/api/users', { params: { role: 'instructor', limit: 50 } }).then(res => setInstructors(res.data.users || [])); }, []);

  const totalPending = payouts.reduce((sum, p) => sum + (p.pending_amount || 0), 0);
  const totalApproved = payouts.reduce((sum, p) => sum + (p.approved_amount || 0), 0);
  const totalAll = payouts.reduce((sum, p) => sum + (p.total_amount || 0), 0);
  const totalSessions = payouts.reduce((sum, p) => sum + (p.sessions?.length || 0), 0);

  const openPayoutConfig = async (instructor) => {
    try {
      const res = await api.get(`/api/payout-config/${instructor.instructor_id}`);
      setConfigForm({
        rate_30min: res.data.rate_30min || 0,
        rate_45min: res.data.rate_45min || 0,
        rate_60min: res.data.rate_60min || 0,
        rate_90min: res.data.rate_90min || 0,
        no_show_rate: res.data.no_show_rate || 0,
      });
    } catch { }
    setConfigDialog({ open: true, instructor });
  };

  const savePayoutConfig = async () => {
    setSavingConfig(true);
    try {
      await api.post('/api/payout-config', {
        instructor_id: configDialog.instructor.instructor_id,
        ...configForm,
      });
      toast.success('Payout config saved');
      setConfigDialog({ open: false, instructor: null });
      fetchPayouts();
    } catch (err) {
      toast.error('Failed to save');
    }
    setSavingConfig(false);
  };

  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    return { value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleDateString('en', { month: 'long', year: 'numeric' }) };
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Finance</h1>
          <p className="text-muted-foreground mt-1">Instructor payouts and session approvals</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* V14.5 Part 19z12 — Filter controls are bound to DRAFT state.
              Changes don't fire until "Apply" is clicked. */}
          <Select value={draftMode} onValueChange={setDraftMode}>
            <SelectTrigger className="w-36" data-testid="finance-filter-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">By month</SelectItem>
              <SelectItem value="custom">Custom range</SelectItem>
            </SelectContent>
          </Select>
          {draftMode === 'month' ? (
            <Select value={draftMonth} onValueChange={setDraftMonth}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                {months.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <>
              <Input
                type="date"
                value={draftFrom}
                max={draftTo}
                onChange={(e) => setDraftFrom(e.target.value)}
                className="w-40"
                data-testid="finance-date-from"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="date"
                value={draftTo}
                min={draftFrom}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setDraftTo(e.target.value)}
                className="w-40"
                data-testid="finance-date-to"
              />
            </>
          )}
          <Select value={draftInstructor} onValueChange={setDraftInstructor}>
            <SelectTrigger className="w-48" data-testid="finance-instructor-filter"><SelectValue placeholder="All Instructors" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Instructors</SelectItem>
              {instructors.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            onClick={applyFilters}
            disabled={!isDirty}
            variant={isDirty ? 'default' : 'outline'}
            data-testid="finance-apply-filters-btn"
          >
            Apply
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
          <DollarSign className="h-5 w-5 text-primary mb-3" />
          <p className="text-2xl font-semibold">INR {totalAll.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Total Payouts</p>
        </CardContent></Card>
        <Card className="rounded-2xl border-amber-200"><CardContent className="pt-5 pb-4">
          <Clock className="h-5 w-5 text-amber-600 mb-3" />
          <p className="text-2xl font-semibold text-amber-900">INR {totalPending.toLocaleString()}</p>
          <p className="text-xs text-amber-700">Pending Approval</p>
        </CardContent></Card>
        <Card className="rounded-2xl border-emerald-200"><CardContent className="pt-5 pb-4">
          <CheckCircle className="h-5 w-5 text-emerald-600 mb-3" />
          <p className="text-2xl font-semibold text-emerald-900">INR {totalApproved.toLocaleString()}</p>
          <p className="text-xs text-emerald-700">Approved</p>
        </CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
          <FileText className="h-5 w-5 text-blue-600 mb-3" />
          <p className="text-2xl font-semibold">{totalSessions}</p>
          <p className="text-xs text-muted-foreground">Total Sessions</p>
        </CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Instructor Payouts</TabsTrigger>
          {/* V14.5 Part 19z11 — Founder ask (2026-06-01): the old simpler
              "Review Sessions" tab and the richer "Adjustments &
              Penalties" tab were doing the same job (approve/reject
              payouts) but with different shapes. Founder asked to
              collapse to ONE tab called "Review Sessions". The
              Adjustments panel is the canonical one (shows both
              payouts AND penalties with type chips, status filter,
              type filter, edit-amount, reject-with-reason,
              reverse-to-pending, and pagination). The old simpler
              tab is retired. Pending-count badge below is sourced
              live from the panel via onCountChange. */}
          <TabsTrigger value="adjustments" data-testid="finance-tab-review">
            Review Sessions
            {pendingReviewCount > 0 && (
              <Badge variant="secondary" className="ml-1 text-[10px]" data-testid="finance-review-count-badge">
                {pendingReviewCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="adjustments" className="mt-4">
          <AdjustmentsPanel
            currentRole={user?.role}
            onPendingCountChange={setPendingReviewCount}
            onMutate={fetchPayouts}
            instructorFilter={instructorFilter}
            filterMode={filterMode}
            month={month}
            dateFrom={dateFrom}
            dateTo={dateTo}
          />
        </TabsContent>

        <TabsContent value="overview" className="mt-4">
          <Card className="rounded-2xl">
            <CardContent className="pt-5">
              {loading ? (
                <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
              ) : payouts.length === 0 ? (
                <div className="text-center py-12"><p className="text-muted-foreground">No completed sessions for this period</p></div>
              ) : (
                <Table data-testid="finance-payouts-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Instructor</TableHead>
                      <TableHead>Sessions</TableHead>
                      <TableHead>Total Payout</TableHead>
                      <TableHead>Pending</TableHead>
                      <TableHead>Approved</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payouts.map(p => (
                      <TableRow key={p.instructor_id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-7 w-7"><AvatarFallback className="text-[10px] bg-primary/10 text-primary">{getInitials(p.instructor_name)}</AvatarFallback></Avatar>
                            <div>
                              <div className="font-medium text-sm">
                                {p.instructor_name}
                                {p.instructor_removed && <Badge variant="outline" className="ml-1.5 text-[10px] bg-slate-100 text-slate-600 border-slate-200" data-testid={`removed-badge-${p.instructor_id}`}>Removed</Badge>}
                              </div>
                              <p className="text-xs text-muted-foreground">{p.instructor_email || (p.instructor_removed ? '(email unavailable)' : '')}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{p.sessions?.length || 0}</TableCell>
                        <TableCell className="font-semibold">INR {(p.total_amount || 0).toLocaleString()}</TableCell>
                        <TableCell><Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">INR {(p.pending_amount || 0).toLocaleString()}</Badge></TableCell>
                        <TableCell><Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">INR {(p.approved_amount || 0).toLocaleString()}</Badge></TableCell>
                        <TableCell>
                          {user?.role === 'superadmin' && (
                            <Button variant="outline" size="sm" onClick={() => openPayoutConfig(p)} data-testid={`config-payout-${p.instructor_id}`}>
                              Set Rates
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payout Config Dialog */}
      <Dialog open={configDialog.open} onOpenChange={(o) => setConfigDialog({ open: o, instructor: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Payout Rates — {configDialog.instructor?.instructor_name}</DialogTitle>
            <p className="text-sm text-muted-foreground">Set per-session payout rates by scheduled duration</p>
          </DialogHeader>
          <div className="space-y-4">
            {[{key:'rate_30min',label:'30 min session'},{key:'rate_45min',label:'45 min session'},{key:'rate_60min',label:'60 min session'},{key:'rate_90min',label:'90 min session'}].map(r => (
              <div key={r.key} className="flex items-center justify-between">
                <label className="text-sm font-medium">{r.label}</label>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">INR</span>
                  <Input type="number" className="w-28" value={configForm[r.key]} onChange={e => setConfigForm({...configForm, [r.key]: parseFloat(e.target.value) || 0})} />
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between">
              <div><label className="text-sm font-medium">No-show payout</label><p className="text-xs text-muted-foreground">When instructor waits 15 min, no student shows</p></div>
              <div className="flex items-center gap-1">
                <span className="text-sm text-muted-foreground">INR</span>
                <Input data-testid="finance-no-show-rate-input" type="number" className="w-28" value={configForm.no_show_rate} onChange={e => setConfigForm({...configForm, no_show_rate: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialog({ open: false, instructor: null })}>Cancel</Button>
            <Button onClick={savePayoutConfig} disabled={savingConfig}>
              {savingConfig ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save Rates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
